import { useEffect, useState } from "react";
import { api } from "../../api";
import { t } from "../../i18n";

type SkillPlan = {
  id: string;
  action: string;
  status: string;
  expiresAt: string;
  request: Record<string, unknown>;
  defaultSelectionError?: string;
  skillReview?: { executable: boolean };
};

export function SkillProposalReview({
  requestId,
  onChange,
}: {
  requestId: string;
  onChange?: () => Promise<void>;
}) {
  const [plan, setPlan] = useState<SkillPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setPlan(null);
    setError("");
    void api<SkillPlan>(
      `/personalization/requests/${encodeURIComponent(requestId)}`,
    )
      .then((value) => {
        if (active) setPlan(value);
      })
      .catch((reason) => {
        if (active)
          setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      active = false;
    };
  }, [requestId]);

  const decide = async (decision: "accept-skill" | "cancel" | "renew") => {
    if (!plan) return;
    setBusy(true);
    setError("");
    try {
      const next =
        decision === "renew"
          ? await api<SkillPlan>("/personalization/requests", {
              method: "POST",
              body: JSON.stringify({
                action: plan.action,
                request: plan.request,
              }),
            })
          : await api<SkillPlan>(
              `/personalization/requests/${encodeURIComponent(plan.id)}/${decision}`,
              { method: "POST" },
            );
      setPlan(next);
      await onChange?.();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      // A proposal may have expired or been decided in another tab since load.
      try {
        setPlan(
          await api<SkillPlan>(
            `/personalization/requests/${encodeURIComponent(plan.id)}`,
          ),
        );
      } catch {
        /* Keep the original error and review content if refresh fails. */
      }
    } finally {
      setBusy(false);
    }
  };
  const active = plan && ["pending", "approved"].includes(plan.status);
  const expired =
    plan &&
    (plan.status === "expired" ||
      (active && Date.parse(plan.expiresAt) <= Date.now()));
  return (
    <div
      className="mt-3 min-w-0 space-y-3 text-stone-800 dark:text-stone-100"
      aria-busy={busy}
    >
      {!plan && !error && (
        <p role="status" className="text-sm">
          {t("Loading skill proposal…")}
        </p>
      )}
      {plan && (
        <>
          <p className="text-sm font-semibold">
            {String(plan.request.name ?? t("Proposed skill"))}
          </p>
          <details>
            <summary className="cursor-pointer text-sm">
              {t("Review skill content")}
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto text-xs wrap-anywhere whitespace-pre-wrap">
              {JSON.stringify(plan.request, null, 2)}
            </pre>
          </details>
          {plan.status === "completed" ? (
            <p role="status" className="text-sm">
              {t("Skill saved. Available for future messages.")}
            </p>
          ) : expired ? (
            <>
              <p className="text-sm">
                {t("This proposal expired. Review it again to allow it.")}
              </p>
              <button
                className="button min-h-11 text-stone-800 dark:text-stone-100"
                disabled={busy}
                onClick={() => void decide("renew")}
              >
                {t("Review again")}
              </button>
            </>
          ) : active ? (
            <>
              <p className="text-sm">
                {(plan.skillReview?.executable ??
                plan.action === "create_executable_skill")
                  ? t(
                      "Allowing saves this executable skill. Administrator permission is required.",
                    )
                  : t(
                      "Allowing saves this personal skill. No administrator review is required.",
                    )}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  className="button-primary min-h-11"
                  disabled={busy}
                  onClick={() => void decide("accept-skill")}
                >
                  {busy ? t("Saving…") : t("Allow")}
                </button>
                <button
                  className="button min-h-11 text-stone-800 dark:text-stone-100"
                  disabled={busy}
                  onClick={() => void decide("cancel")}
                >
                  {t("Deny")}
                </button>
              </div>
            </>
          ) : (
            <p role="status" className="text-sm">
              {plan.status === "cancelled"
                ? t("Skill proposal denied.")
                : t("This skill proposal is no longer active.")}
            </p>
          )}
          {plan.defaultSelectionError && (
            <p role="status" className="text-sm">
              {t("Skill saved, but could not select it as a default:")}{" "}
              {plan.defaultSelectionError}
            </p>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
